// ============================================================================
// Hooks — Wiki pública da Base de Conhecimento (F3 Bloco A)
// Consomem RPCs kb_public_* que não exigem autenticação.
// ============================================================================

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface KbPublicTenant {
  tenant_id: string;
  tenant_slug: string;
  tenant_name: string;
  spaces_count: number;
  pages_count: number;
}

export interface KbPublicSpace {
  space_id: string;
  space_slug: string;
  space_name: string;
  description: string | null;
  icon: string | null;
  color: string | null;
  pages_count: number;
  last_updated_at: string;
}

export interface KbPublicPageListItem {
  page_id: string;
  page_slug: string;
  title: string;
  summary: string | null;
  parent_id: string | null;
  position_order: number;
  updated_at: string;
  published_at: string | null;
}

export interface KbPublicPage {
  page_id: string;
  tenant_id: string;
  tenant_name: string;
  space_id: string;
  space_name: string;
  space_slug: string;
  page_slug: string;
  title: string;
  summary: string | null;
  content_markdown: string | null;
  content_html: string | null;
  locale: string;
  version_number: number;
  published_at: string | null;
  updated_at: string;
  view_count: number;
}

/** Metadados do tenant + contagens públicas para a landing /kb/:tenantSlug. */
export function useKbPublicTenant(tenantSlug: string | undefined) {
  return useQuery({
    queryKey: ["kb-public", "tenant", tenantSlug],
    enabled: !!tenantSlug,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("kb_public_get_tenant", {
        p_tenant_slug: tenantSlug!,
      });
      if (error) throw error;
      return (data?.[0] ?? null) as KbPublicTenant | null;
    },
    staleTime: 5 * 60_000,
  });
}

/** Lista de spaces com pelo menos uma página pública publicada. */
export function useKbPublicSpaces(tenantSlug: string | undefined) {
  return useQuery({
    queryKey: ["kb-public", "spaces", tenantSlug],
    enabled: !!tenantSlug,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("kb_public_list_spaces", {
        p_tenant_slug: tenantSlug!,
      });
      if (error) throw error;
      return (data ?? []) as KbPublicSpace[];
    },
    staleTime: 5 * 60_000,
  });
}

/** Lista de páginas públicas de um space. */
export function useKbPublicPages(
  tenantSlug: string | undefined,
  spaceSlug: string | undefined,
) {
  return useQuery({
    queryKey: ["kb-public", "pages", tenantSlug, spaceSlug],
    enabled: !!tenantSlug && !!spaceSlug,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("kb_public_list_pages", {
        p_tenant_slug: tenantSlug!,
        p_space_slug: spaceSlug!,
      });
      if (error) throw error;
      return (data ?? []) as KbPublicPageListItem[];
    },
    staleTime: 2 * 60_000,
  });
}

/** Conteúdo completo de uma página pública (incrementa view_count no backend). */
export function useKbPublicPage(
  tenantSlug: string | undefined,
  spaceSlug: string | undefined,
  pageSlug: string | undefined,
) {
  return useQuery({
    queryKey: ["kb-public", "page", tenantSlug, spaceSlug, pageSlug],
    enabled: !!tenantSlug && !!spaceSlug && !!pageSlug,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("kb_public_get_page", {
        p_tenant_slug: tenantSlug!,
        p_space_slug: spaceSlug!,
        p_page_slug: pageSlug!,
      });
      if (error) throw error;
      return (data?.[0] ?? null) as KbPublicPage | null;
    },
    // Sem cache: aciona incremento de views a cada visita real.
    staleTime: 0,
    gcTime: 60_000,
  });
}
